import React, { useEffect, useRef, useState } from "react";
import grapesjs from "grapesjs";
import gjsPresetNewsletter from "grapesjs-preset-newsletter";
import "grapesjs/dist/css/grapes.min.css";
import logo from "../../../assets/logo-main-light.svg";
import {
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  message,
  theme,
} from "antd";
import dayjs from "dayjs";
import {
  DesktopOutlined,
  EyeOutlined,
  MobileOutlined,
  RedoOutlined,
  SaveOutlined,
  TabletOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import { privateGetApi, privatePostApi } from "../../../utils/apiHelper";
import { useDispatch } from "react-redux";
import {
  createCampaign,
  updateCampaign,
} from "../../../redux/actions/campaignsAction";

const { Title, Text } = Typography;

export default function GuidedEmailEditor() {
  const { token } = theme.useToken();
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [campaignSubject, setCampaignSubject] = useState("");
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [sendMode, setSendMode] = useState("save");
  const [scheduledSendAt, setScheduledSendAt] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateVisibility, setTemplateVisibility] = useState("private");
  const [activeDevice, setActiveDevice] = useState("Desktop");

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : {};

  const emailSettings = localStorage.getItem("emailSettings")
    ? JSON.parse(localStorage.getItem("emailSettings"))
    : null;

  const windowOrigin = window.location.origin;

  const isPaidPlan = user.plan && user.plan !== "free";

  const getDefaultBrandedFooter = () =>
    '<div style=\'max-width: 600px; margin: auto;\'><div><p style="font-size: 10pt; color: #bfbfbf; text-align: center">You are receiving this email because you have visited our site or opted for our newsletter. Make sure our messages get to your Inbox (and not your bulk or junk folders).</p><p style="font-size: 10pt; color: #bfbfbf; text-align: center"><a href="' +
    windowOrigin +
    "/unsubscribe/" +
    user.emailAddress +
    "/" +
    user.emailAddress +
    '">Unsubscribe</a> | <a href="' +
    windowOrigin +
    '/privacy-policy">Privacy Policy</a></p><p style="font-size: 10pt; color: #bfbfbf; text-align: center">Powered by<img src="' +
    logo +
    '" style="display: block; margin-top: 5px; margin-left: auto; margin-right: auto;" height="30"/></p></div></div>';

  const addLockedFooterComponent = (editor) => {
    const footerHtml = getDefaultBrandedFooter();
    const added = editor.addComponents(footerHtml);

    const lockTree = (comp) => {
      if (!comp) return;
      comp.set({
        removable: false,
        draggable: false,
        copyable: false,
        editable: false,
        hoverable: false,
        selectable: false,
      });
      comp.components().forEach((child) => lockTree(child));
    };

    const list = Array.isArray(added) ? added : [added];
    list.forEach((comp) => lockTree(comp));
  };

  const getEditorHtml = () => {
    const editor = editorRef.current;
    if (!editor) return "";
    const css = editor.getCss() || "";
    const rawHtml = editor.getHtml() || "";

    const storedFooter = emailSettings?.emailFooter;
    const storedWhitelabel = isPaidPlan && emailSettings?.removeWhitelabel === true;

    // Whitelabel users' custom footer is seeded into the canvas (editable)
    // by enforceFooter() on load, so it's normally already part of rawHtml.
    // Safety net in case that hasn't run yet (e.g. saved before "load" fired).
    if (storedWhitelabel) {
      let html = rawHtml || "";
      if (!html.includes(storedFooter)) {
        html += storedFooter;
      }
      return `<style>${css}</style>${html}`;
    }

    const hasBranding =
      rawHtml &&
      rawHtml.includes("/unsubscribe/") &&
      rawHtml.includes("/privacy-policy") &&
      rawHtml.toLowerCase().includes("powered by") &&
      /height\s*=\s*['"]30['"]/i.test(rawHtml);

    if (!hasBranding) {
      // Safety net: if branding footer is somehow missing at save time, append it.
      return `<style>${css}</style>${rawHtml}${getDefaultBrandedFooter()}`;
    }

    return `<style>${css}</style>${rawHtml}`;
  };

  const hasMeaningfulContent = (html) => {
    if (!html) return false;
    return Boolean(
      html
        .replace(/<[^>]+>/g, "")
        .replaceAll("&nbsp;", "")
        .trim(),
    );
  };

  const validateCampaignRequirements = () => {
    const html = getEditorHtml();
    if (!campaignSubject.trim()) {
      message.error("Campaign subject is required for campaign save/send.");
      return null;
    }
    if (!campaignRecipients.length) {
      message.error(
        "Select at least one recipient tag for campaign save/send.",
      );
      return null;
    }
    if (!hasMeaningfulContent(html)) {
      message.error("Add content before campaign save/send.");
      return null;
    }
    return html;
  };

  useEffect(() => {
    if (editorRef.current) return;
    if (!document.getElementById("mailinflow-editor-cleanup-style")) {
      const styleTag = document.createElement("style");
      styleTag.id = "mailinflow-editor-cleanup-style";
      styleTag.innerHTML = `
        #mailinflow-editor-canvas .gjs-pn-panels,
        #mailinflow-editor-canvas .gjs-pn-devices-c,
        #mailinflow-editor-canvas .gjs-pn-commands,
        #mailinflow-editor-canvas .gjs-pn-options {
          display: none !important;
        }

        /* Make the GrapesJS canvas fill its column with no gap on the right */
        #mailinflow-editor-canvas .gjs-cv-canvas {
          top: 0 !important;
          height: 100% !important;
          margin: 0 !important;
          width: 100% !important;
        }

        #mailinflow-editor-canvas .gjs-frame-wrapper {
          top: 0 !important;
        }

        #mailinflow-editor-canvas .gjs-editor-cont {
          overflow: hidden;
        }
      `;
      document.head.appendChild(styleTag);
    }

    const editor = grapesjs.init({
      container: "#mailinflow-editor-canvas",
      fromElement: false,
      height: "calc(100vh - 260px)",
      storageManager: false,
      panels: { defaults: [] },
      deviceManager: {
        devices: [
          { name: "Desktop", width: "" },
          { name: "Tablet", width: "768px", widthMedia: "992px" },
          { name: "Mobile portrait", width: "360px", widthMedia: "575px" },
        ],
      },
      blockManager: { appendTo: "#mailinflow-editor-blocks" },
      styleManager: { appendTo: "#mailinflow-editor-styles" },
      traitManager: { appendTo: "#mailinflow-editor-traits" },
      layerManager: { appendTo: "#mailinflow-editor-layers" },
      selectorManager: { appendTo: "#mailinflow-editor-selectors" },
      plugins: [gjsPresetNewsletter],
      pluginsOpts: {
        [gjsPresetNewsletter]: {
          modalLabelImport: "Paste template code",
          modalLabelExport: "Copy HTML",
          codeViewerTheme: "hopscotch",
          blocks: [
            "sect100",
            "sect50",
            "sect30",
            "sect37",
            "button",
            "divider",
            "text",
            "text-sect",
            "image",
            "quote",
            "link",
            "grid-items",
            "list-items",
            "nav-items",
            "social-items",
          ],
        },
      },
    });
    editorRef.current = editor;

    const enforceFooter = () => {
      // Ensure a footer is visible in the visual canvas by default, for
      // both whitelabel and non-whitelabel users.
      const storedFooter = emailSettings?.emailFooter;
      const storedWhitelabel = isPaidPlan && emailSettings?.removeWhitelabel === true;
      const currentHtml = editor.getHtml() || "";

      if (storedWhitelabel) {
        // Whitelabel users: seed their saved custom footer, left editable
        // (unlike the locked default), so campaigns actually pick up
        // whatever they configured under Settings instead of starting blank.
        if (storedFooter && !currentHtml.includes(storedFooter)) {
          editor.addComponents(storedFooter);
        }
        return;
      }

      const hasBranding =
        currentHtml.includes("/unsubscribe/") &&
        currentHtml.includes("/privacy-policy") &&
        currentHtml.toLowerCase().includes("powered by") &&
        /height\s*=\s*['"]30['"]/i.test(currentHtml);

      if (!hasBranding) {
        // Free or paid without whitelabel: enforce a locked, non-editable Mailinflow footer.
        addLockedFooterComponent(editor);
      }
    };

    editor.on("load", () => {
      editor.runCommand("sw-visibility");
      editor.setDevice("Desktop");
      setActiveDevice("Desktop");

      // If user is starting fresh, seed the canvas with the same
      // one-column section used by the "1 section" template block.
      const hasAnyComponent = editor.getWrapper().components().length > 0;
      if (!hasAnyComponent) {
        const oneColBlock = editor.BlockManager.get("sect100");
        if (oneColBlock) {
          editor.addComponents(oneColBlock.get("content") || "");
        }
      }

      enforceFooter();
    });
    // Editor and footer init run once on mount; deps intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    privateGetApi("/templates", {})
      .then((res) => setTemplates(res?.data?.result || []))
      .catch(() => setTemplates([]));
    privateGetApi("/contacts/tags", {})
      .then((res) => setTagOptions(res?.data?.result?.records || []))
      .catch(() => setTagOptions([]));
  }, []);

  const loadTemplate = async (templateId) => {
    try {
      const res = await privateGetApi(`/templates/${templateId}`, {});
      const tpl = res?.data?.result;
      if (!tpl || !editorRef.current) return;
      setCampaignSubject(tpl.subject || "");
      setTemplateSubject(tpl.subject || "");
      editorRef.current.setComponents(tpl.content || "");
      message.success("Template loaded.");
    } catch (e) {
      message.error("Failed to load template.");
    }
  };

  const ensureDraftSaved = async () => {
    const html = validateCampaignRequirements();
    if (!html) {
      return null;
    }
    const payload = {
      subject: campaignSubject.trim(),
      message: html,
      status: { type: "warning", description: "Draft" },
      tags: campaignRecipients,
    };
    if (campaignId) {
      await dispatch(updateCampaign(payload, campaignId));
      return campaignId;
    }
    const created = await dispatch(createCampaign(payload));
    const id = created?._id || null;
    if (id) setCampaignId(id);
    return id;
  };

  const handleSaveTemplate = async () => {
    const html = getEditorHtml();
    if (!templateName.trim()) {
      message.error("Enter template name.");
      return;
    }
    if (!hasMeaningfulContent(html)) {
      message.error("Template content is empty.");
      return;
    }
    try {
      setLoading(true);
      await privatePostApi("/templates", {
        name: templateName.trim(),
        subject: templateSubject.trim(),
        content: html,
        category: "custom",
        visibility: templateVisibility,
      });
      const list = await privateGetApi("/templates", {});
      setTemplates(list?.data?.result || []);
      setTemplateName("");
      setTemplateSubject("");
      setTemplateVisibility("private");
      setTemplateModalOpen(false);
      message.success("Template saved.");
    } catch {
      message.error("Failed to save template.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCampaignFromModal = async () => {
    if (sendMode === "schedule" && !scheduledSendAt) {
      message.error("Please pick a date and time to schedule the send.");
      return;
    }
    try {
      setLoading(true);
      const id = await ensureDraftSaved();
      if (!id) return;
      if (sendMode === "send") {
        await dispatch(
          updateCampaign(
            {
              status: { type: "processing", description: "Pending" },
              subject: campaignSubject.trim(),
            },
            id,
          ),
        );
        message.success("Campaign queued.");
      } else if (sendMode === "schedule") {
        await dispatch(
          updateCampaign(
            {
              status: { type: "warning", description: "Scheduled" },
              subject: campaignSubject.trim(),
              scheduledSendAt: scheduledSendAt.toISOString(),
            },
            id,
          ),
        );
        message.success("Campaign scheduled.");
      } else {
        message.success("Campaign saved.");
      }
      setCampaignModalOpen(false);
      setSendMode("save");
      setScheduledSendAt(null);
    } catch {
      message.error("Failed to save campaign.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetDevice = (device) => {
    if (!editorRef.current) return;
    editorRef.current.setDevice(device);
    setActiveDevice(device);
  };

  const handleUndo = () => {
    editorRef.current?.UndoManager.undo();
  };

  const handleRedo = () => {
    editorRef.current?.UndoManager.redo();
  };

  return (
    <div className="Editor">
      <Space
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: "0 0.5rem 0.5rem 0.5rem",
        }}
      >
        <div>
          <Title level={5} style={{ marginBottom: 0 }}>
            Editor
          </Title>
          <Text type="secondary">
            Drag & drop builder for campaign creation or use to create email
            templates and save for later.
          </Text>
        </div>
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => setPreviewOpen(true)}>
            Preview
          </Button>
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={() => {
              setCampaignModalOpen(true);
              setSendMode("save");
              setScheduledSendAt(null);
            }}
            loading={loading}
          >
            Save as Campaign
          </Button>
          <Button
            onClick={() => {
              setTemplateName("");
              setTemplateSubject(campaignSubject);
              setTemplateVisibility("private");
              setTemplateModalOpen(true);
            }}
            loading={loading}
          >
            Save as Template
          </Button>
        </Space>
      </Space>

      <Row gutter={12}>
        <Col xs={24} md={5}>
          <div
            style={{
              maxHeight: "calc(100vh - 260px)",
              overflow: "auto",
            }}
          >
            <Collapse
              defaultActiveKey={["blocks", "templates"]}
              style={{ background: "transparent", border: "none" }}
              items={[
                {
                  key: "blocks",
                  label: "Blocks",
                  forceRender: true /* keep in DOM so GrapesJS blockManager can append and persist */,
                  children: (
                    <div
                      style={{
                        padding: 8,
                        maxHeight: 320,
                        overflow: "auto",
                      }}
                    >
                      <div id="mailinflow-editor-blocks" />
                    </div>
                  ),
                },
                {
                  key: "templates",
                  label: "Templates",
                  children: (
                    <div
                      style={{
                        padding: 8,
                        maxHeight: 280,
                        overflow: "auto",
                      }}
                    >
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {templates.map((tpl) => (
                          <div
                            key={tpl.id}
                            style={{
                              border: `1px solid ${token.colorBorderSecondary}`,
                              borderRadius: token.borderRadius,
                              padding: 8,
                            }}
                          >
                            <Space
                              direction="vertical"
                              style={{ width: "100%" }}
                            >
                              <Space>
                                <Text strong>{tpl.name}</Text>
                                {tpl.isSystem ? (
                                  <Tag>System</Tag>
                                ) : tpl.isOwner ? (
                                  <Tag color="cyan">
                                    My{" "}
                                    {tpl.visibility === "public"
                                      ? "(Public)"
                                      : "(Private)"}
                                  </Tag>
                                ) : (
                                  <Tag color="green">Public</Tag>
                                )}
                              </Space>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {tpl.subject || "No subject"}
                              </Text>
                              <Button
                                type="link"
                                style={{ padding: 0 }}
                                onClick={() => loadTemplate(tpl.id)}
                              >
                                Use Template
                              </Button>
                            </Space>
                          </div>
                        ))}
                      </Space>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Col>
        <Col xs={24} md={19}>
          <Card
            size="small"
            title="Canvas"
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<DesktopOutlined />}
                  type={activeDevice === "Desktop" ? "primary" : "default"}
                  onClick={() => handleSetDevice("Desktop")}
                />
                <Button
                  size="small"
                  icon={<TabletOutlined />}
                  type={activeDevice === "Tablet" ? "primary" : "default"}
                  onClick={() => handleSetDevice("Tablet")}
                />
                <Button
                  size="small"
                  icon={<MobileOutlined />}
                  type={
                    activeDevice === "Mobile portrait" ? "primary" : "default"
                  }
                  onClick={() => handleSetDevice("Mobile portrait")}
                />
                <Button
                  size="small"
                  icon={<UndoOutlined />}
                  onClick={handleUndo}
                >
                  Undo
                </Button>
                <Button
                  size="small"
                  icon={<RedoOutlined />}
                  onClick={handleRedo}
                >
                  Redo
                </Button>
              </Space>
            }
            bodyStyle={{ padding: 0, minHeight: 520 }}
          >
            <div style={{ display: "flex", minHeight: 520 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div id="mailinflow-editor-canvas" />
              </div>
              <div
                style={{
                  width: 280,
                  borderLeft: `1px solid ${token.colorBorderSecondary}`,
                  padding: 8,
                  background: token.colorFillSecondary,
                }}
              >
                <Card
                  title="Styles"
                  size="small"
                  bodyStyle={{
                    padding: 8,
                    maxHeight: "32vh",
                    overflow: "auto",
                  }}
                >
                  <div id="mailinflow-editor-styles" />
                </Card>
                <Card
                  title="Properties"
                  size="small"
                  style={{ marginTop: 10 }}
                  bodyStyle={{
                    padding: 8,
                    maxHeight: "22vh",
                    overflow: "auto",
                  }}
                >
                  <div id="mailinflow-editor-traits" />
                </Card>
                <Card
                  title="Layers"
                  size="small"
                  style={{ marginTop: 10 }}
                  bodyStyle={{
                    padding: 8,
                    maxHeight: "18vh",
                    overflow: "auto",
                  }}
                >
                  <div id="mailinflow-editor-layers" />
                </Card>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Email Preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width="min(1000px, 90vw)"
      >
        <iframe
          title="preview"
          style={{
            width: "100%",
            height: 600,
            border: `1px solid ${token.colorBorderSecondary}`,
          }}
          srcDoc={getEditorHtml()}
        />
      </Modal>

      <Modal
        title="Save as Campaign"
        open={campaignModalOpen}
        onCancel={() => setCampaignModalOpen(false)}
        onOk={handleSaveCampaignFromModal}
        okText={
          sendMode === "send"
            ? "Save and Send"
            : sendMode === "schedule"
              ? "Save and Schedule"
              : "Save Campaign"
        }
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <Form.Item label="Campaign Subject" required>
            <Input
              placeholder="Enter campaign subject"
              value={campaignSubject}
              onChange={(e) => setCampaignSubject(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Recipients (Tags)" required>
            <Select
              mode="multiple"
              placeholder="Select recipient tags"
              value={campaignRecipients}
              onChange={setCampaignRecipients}
              options={tagOptions.map((t) => ({ value: t, label: t }))}
            />
          </Form.Item>
          <Divider style={{ margin: "12px 0" }} />
          <Text type="secondary">
            Choose whether to only save, send now, or schedule for later.
          </Text>
          <Row style={{ marginTop: 8 }} gutter={8}>
            <Col>
              <Select
                value={sendMode}
                onChange={(val) => setSendMode(val)}
                options={[
                  { value: "save", label: "Save as draft" },
                  { value: "send", label: "Save and send now" },
                  { value: "schedule", label: "Schedule for later" },
                ]}
                style={{ width: 220 }}
              />
            </Col>
            {sendMode === "schedule" && (
              <Col>
                <DatePicker
                  showTime
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  value={scheduledSendAt}
                  onChange={setScheduledSendAt}
                  placeholder="Select send date & time"
                />
              </Col>
            )}
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Save as Template"
        open={templateModalOpen}
        onCancel={() => setTemplateModalOpen(false)}
        onOk={handleSaveTemplate}
        okText="Save Template"
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <Form.Item label="Template Name" required>
            <Input
              placeholder="Enter template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Template Subject (optional)">
            <Input
              placeholder="Optional subject line"
              value={templateSubject}
              onChange={(e) => setTemplateSubject(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Visibility">
            <Radio.Group
              value={templateVisibility}
              onChange={(e) => setTemplateVisibility(e.target.value)}
            >
              <Radio value="private">Private (only your org)</Radio>
              <Radio value="public">Public (shared with all orgs)</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
